/**
 * <FileUploadZone /> — drag-drop + click-to-pick upload surface.
 *
 * Per BUILD_READINESS_M29.md acceptance criterion 8: accepts
 * Excel + CSV, any format, multi-file (up to 10 / 50MB each /
 * 100MB total — enforced server-side).
 *
 * On successful upload: navigates to /operations/import?session_id=X
 * so the dashboard + the rest of the wizard takes over.
 */

import { useMutation } from "@tanstack/react-query";
import { AlertCircle, FileUp, Loader2, Sparkles, X } from "lucide-react";
import { useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { api } from "../../lib/api";
import { useLegalEntityId } from "../../lib/legalEntity";

const _ACCEPTED_EXTENSIONS = ".csv,.xlsx,.xls";

export function FileUploadZone() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const le = useLegalEntityId();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  const upload = useMutation({
    mutationFn: () =>
      api.importCreateSession(files, {
        legalEntityId: le ?? null,
      }),
    onSuccess: (session) => {
      navigate(
        `/operations/import?session_id=${session.id}`,
        { replace: true },
      );
    },
  });

  function _onDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }

  function _onDragLeave(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }

  function _onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const dropped = Array.from(e.dataTransfer.files ?? []);
    if (dropped.length > 0) {
      setFiles((prev) => [...prev, ...dropped]);
    }
  }

  function _onPick(e: ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    if (picked.length > 0) {
      setFiles((prev) => [...prev, ...picked]);
    }
    // Reset the input so picking the same file twice in a row works.
    e.target.value = "";
  }

  function _removeFile(idx: number) {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  const totalBytes = files.reduce((acc, f) => acc + f.size, 0);
  const tooBig = totalBytes > 100 * 1024 * 1024;
  const tooMany = files.length > 10;
  const blocked = tooBig || tooMany;

  return (
    <section className="rounded-xl border border-border-subtle bg-surface-default p-6 space-y-4">
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            fileInputRef.current?.click();
          }
        }}
        onDragOver={_onDragOver}
        onDragLeave={_onDragLeave}
        onDrop={_onDrop}
        className={[
          "flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-8 text-center cursor-pointer transition-colors",
          isDragging
            ? "border-brand-blue bg-ai-bg"
            : "border-border-subtle hover:border-brand-blue hover:bg-surface-sunken",
        ].join(" ")}
        aria-label={t("app.fuz_aria")}
      >
        <FileUp
          size={32}
          className={isDragging ? "text-brand-blue" : "text-text-tertiary"}
          aria-hidden="true"
        />
        <p className="text-sm font-medium text-text-primary">
          {isDragging
            ? t("app.fuz_drop_file")
            : t("app.fuz_drop_any")}
        </p>
        <p className="text-xs text-text-tertiary">
          {t("app.fuz_accepted_desc")}
        </p>
        <p className="text-[10px] text-text-tertiary">
          {t("app.fuz_caps")}
        </p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={_ACCEPTED_EXTENSIONS}
          onChange={_onPick}
          className="hidden"
          aria-hidden="true"
        />
      </div>

      {files.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-text-secondary">
            {t(
              files.length === 1 ? "app.fuz_ready_one" : "app.fuz_ready_many",
              { n: files.length, size: _formatBytes(totalBytes) },
            )}
          </p>
          <ul className="space-y-1">
            {files.map((f, idx) => (
              <li
                key={`${f.name}-${idx}`}
                className="flex items-center justify-between gap-2 rounded-md bg-surface-sunken px-2.5 py-1.5"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <FileUp
                    size={12}
                    className="shrink-0 text-text-tertiary"
                    aria-hidden="true"
                  />
                  <span className="truncate text-xs text-text-primary">
                    {f.name}
                  </span>
                  <span className="text-[10px] text-text-tertiary shrink-0">
                    {_formatBytes(f.size)}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => _removeFile(idx)}
                  className="rounded text-text-tertiary hover:text-rose-700"
                  aria-label={t("app.fuz_remove_aria", { name: f.name })}
                >
                  <X size={12} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {blocked && (
        <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-900">
          <AlertCircle size={14} className="mt-0.5 shrink-0 text-rose-700" />
          <div>
            {tooMany && <p>{t("app.fuz_too_many")}</p>}
            {tooBig && (
              <p>
                {t("app.fuz_too_big", { size: _formatBytes(totalBytes) })}
              </p>
            )}
          </div>
        </div>
      )}

      {upload.error && (
        <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-900">
          <AlertCircle size={14} className="mt-0.5 shrink-0 text-rose-700" />
          <span>{(upload.error as Error).message}</span>
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-1 text-[11px] text-ai-deep">
          <Sparkles size={11} className="text-ai" aria-hidden="true" />
          {t("app.fuz_ai_hint")}
        </p>
        <button
          type="button"
          disabled={files.length === 0 || blocked || upload.isPending}
          onClick={() => upload.mutate()}
          className="inline-flex items-center gap-1.5 rounded-md bg-brand-blue px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          {upload.isPending && (
            <Loader2
              size={12}
              className="motion-safe:animate-spin"
              aria-hidden="true"
            />
          )}
          {upload.isPending
            ? t("app.fuz_uploading2")
            : files.length === 0
              ? t("app.fuz_upload_zero")
              : t(
                  files.length === 1 ? "app.fuz_upload_one" : "app.fuz_upload_many",
                  { n: files.length },
                )}
        </button>
      </div>
    </section>
  );
}

function _formatBytes(n: number): string {
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}KB`;
  return `${(n / 1024 / 1024).toFixed(1)}MB`;
}
